import { useEffect, useState } from 'react';

/**
 * True when the app is running as an installed PWA (standalone display), where
 * there's no browser refresh button — `(display-mode: standalone)` on Chrome/
 * Android/desktop, `navigator.standalone` on iOS.
 */
export function useIsStandalone(): boolean {
  const [standalone, setStandalone] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    const compute = () =>
      mq.matches || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    setStandalone(compute());
    const onChange = () => setStandalone(compute());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return standalone;
}

/**
 * Drop any cached app shell / service worker, then reload to the latest deploy.
 * The server serves `index.html` with `no-store`, so the reload pulls fresh,
 * content-hashed assets; clearing caches/SW is best-effort future-proofing.
 */
export async function forceReloadApp(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Best-effort — reload regardless.
  }
  window.location.reload();
}
